import {
	UserOutlined,
	TeamOutlined,
	LockOutlined,
	AppstoreOutlined,
	ClockCircleOutlined,
	TagOutlined,
	VideoCameraOutlined,
	InboxOutlined,
	CloudOutlined,
	UnorderedListOutlined,
	SafetyOutlined
} from '@ant-design/icons';
import { ZezamiiIcon } from 'zComponents/atoms/ZezamiiIcon';
import { zezamiiAccess } from 'theme/brandTokens';

/** Central entity registry: keys, labels, icons, colours and ID fields — never hardcode 'person' etc. */

// ── Entity definitions ──────────────────────────────────────
export const ENTITY = Object.freeze({
	PERSON: {
		key: 'person',
		label: 'People',
		singular: 'Person',
		idKey: 'personID',
		tabKey: 'people',
		icon: (color, size = 13) => <ZezamiiIcon name='user' size={size} color={color} strokeWidth={2} />,
		tabIcon: UserOutlined,
		// Person chips are the Zezamii access cyan, read app-wide (~23 places); 7.56:1 clears the 7:1 AAA bar.
		tagBg: zezamiiAccess.access1,
		tagColor: zezamiiAccess.access5
	},
	// A person who is visiting: the person palette with a user-plus, so a visitor reads apart at a glance.
	VISITOR: {
		key: 'visitor',
		label: 'Visitors',
		singular: 'Visitor',
		idKey: 'personID',
		tabKey: 'visitors',
		icon: (color, size = 13) => <ZezamiiIcon name='visitor' size={size} color={color} strokeWidth={2} />,
		tagBg: zezamiiAccess.access1,
		tagColor: zezamiiAccess.access5
	},
	TEAM: {
		key: 'team',
		label: 'Teams',
		singular: 'Team',
		idKey: 'teamID',
		tabKey: 'teams',
		icon: (color, size = 13) => <ZezamiiIcon name='users' size={size} color={color} strokeWidth={2} />,
		tabIcon: TeamOutlined,
		tagBg: '#E1F5EE',
		tagColor: '#0F6E56'
	},
	DEVICE: {
		key: 'device',
		label: 'Devices',
		singular: 'Device',
		idKey: 'deviceID',
		tabKey: 'devices',
		icon: (color, size = 13, iconName) => (
			<ZezamiiIcon name={iconName || 'lock'} size={size} color={color} strokeWidth={2} />
		),
		tabIcon: LockOutlined,
		tagBg: '#FFF4E6',
		tagColor: '#B8730E'
	},
	GROUP: {
		key: 'group',
		label: 'Groups',
		singular: 'Group',
		idKey: 'assetGroupID',
		tabKey: 'groups',
		icon: (color, size = 13) => <ZezamiiIcon name='space' size={size} color={color} strokeWidth={2} />,
		tabIcon: AppstoreOutlined,
		tagBg: '#F3F0FF',
		tagColor: '#6B21A8'
	},
	// Inner Range List container (DoorList/LiftFloorList/LiftCarList). Distinct avatar from GROUP so a
	// nested List reads as a container rather than a group across every table/drawer/modal.
	LIST: {
		key: 'list',
		label: 'Lists',
		singular: 'List',
		idKey: 'assetGroupID',
		tabKey: 'lists',
		icon: (color, size = 13) => <UnorderedListOutlined style={{ fontSize: size, color }} />,
		tabIcon: UnorderedListOutlined,
		tagBg: '#EEF4FF',
		tagColor: '#3538CD'
	},
	SCHEDULE: {
		key: 'schedule',
		label: 'Schedules',
		singular: 'Schedule',
		idKey: 'accessWindowID',
		tabKey: 'schedules',
		icon: (color, size = 13) => <ZezamiiIcon name='calendar' size={size} color={color} strokeWidth={2} />,
		tabIcon: ClockCircleOutlined,
		tagBg: '#EFF6FF',
		tagColor: '#1D4ED8'
	},
	SITE: {
		key: 'site',
		label: 'Sites',
		singular: 'Site',
		idKey: 'siteID',
		tabKey: 'sites',
		icon: (color, size = 13) => <ZezamiiIcon name='building' size={size} color={color} strokeWidth={2} />,
		tabIcon: AppstoreOutlined,
		// ⚠️ #0E7490 is also the Cameras family fill; the Cameras browser tells them apart by surface only.
		// Never add a third tag on this hex — if a site must sit beside a camera, move SITE.
		tagBg: '#E0F7FA',
		tagColor: '#0E7490'
	},
	ROLE: {
		key: 'role',
		label: 'Roles',
		singular: 'Role',
		idKey: 'permissionID',
		tabKey: 'roles',
		icon: (color, size = 13) => <ZezamiiIcon name='shield-check' size={size} color={color} strokeWidth={2} />,
		tabIcon: SafetyOutlined,
		tagBg: '#F1F5F9',
		tagColor: '#334155'
	},
	SPACE: {
		key: 'space',
		label: 'Spaces',
		singular: 'Space',
		idKey: 'floorID',
		tabKey: 'spaces',
		icon: (color, size = 13) => <ZezamiiIcon name='space' size={size} color={color} strokeWidth={2} />,
		tabIcon: AppstoreOutlined,
		tagBg: '#ECFDF5',
		tagColor: '#0D9488'
	},
	CAMERA: {
		key: 'camera',
		label: 'Cameras',
		singular: 'Camera',
		idKey: 'deviceID',
		tabKey: 'cameras',
		icon: (color, size = 13) => <ZezamiiIcon name='camera' size={size} color={color} strokeWidth={2} />,
		tabIcon: VideoCameraOutlined,
		// The Cameras device family, not a free hue: a camera avatar must match its floor-plan pin.
		// EntityAvatar only auto-applies family colours to key 'device', so this one is spelled out.
		tagBg: '#ECFEFF',
		tagColor: '#0E7490'
	},
	CATEGORY: {
		key: 'category',
		label: 'Categories',
		singular: 'Category',
		idKey: 'categoryID',
		tabKey: 'categories',
		icon: (color, size = 13) => <ZezamiiIcon name='settings' size={size} color={color} strokeWidth={2} />,
		tabIcon: TagOutlined,
		tagBg: '#FFF7ED',
		tagColor: '#C2410C'
	},
	ASSET: {
		key: 'asset',
		label: 'Assets',
		singular: 'Asset',
		idKey: 'deviceID',
		tabKey: 'assets',
		icon: (color, size = 13) => <ZezamiiIcon name='asset' size={size} color={color} strokeWidth={2} />,
		tabIcon: InboxOutlined,
		tagBg: '#FFF7ED',
		tagColor: '#9A3412'
	},
	ACTIVITY: {
		key: 'activity',
		label: 'Activity',
		singular: 'Activity',
		idKey: null,
		tabKey: 'activity',
		icon: (color, size = 13) => <ZezamiiIcon name='scroll-text' size={size} color={color} strokeWidth={2} />,
		tabIcon: ClockCircleOutlined,
		tagBg: '#F5F3FF',
		tagColor: '#7C3AED'
	},
	AUDIT: {
		key: 'audit',
		label: 'Audit Log',
		singular: 'Audit event',
		idKey: 'auditID',
		tabKey: 'audit',
		icon: (color, size = 13) => <ZezamiiIcon name='audit-log' size={size} color={color} strokeWidth={2} />,
		tabIcon: ClockCircleOutlined,
		tagBg: '#F4F4F5',
		tagColor: '#18181B'
	}
});

// ── Lookup by key string ────────────────────────────────────
// ENTITY_TYPES['person'] === ENTITY.PERSON
export const ENTITY_TYPES = Object.freeze(
	Object.values(ENTITY).reduce((acc, entity) => {
		acc[entity.key] = entity;
		return acc;
	}, {})
);

// ── Subject / Resource groupings ────────────────────────────
export const SUBJECT_TYPES = Object.freeze({
	PERSON: ENTITY.PERSON.key,
	TEAM: ENTITY.TEAM.key
});

export const RESOURCE_TYPES = Object.freeze({
	DEVICE: ENTITY.DEVICE.key,
	GROUP: ENTITY.GROUP.key
});

// ── Credential type definitions ─────────────────────────────
export const CREDENTIAL = Object.freeze({
	MOBILE: {
		key: 'mobile',
		label: 'Mobile',
		singular: 'Mobile Key',
		icon: (color, size = 13) => <ZezamiiIcon name='mobile-credential' size={size} color={color} strokeWidth={2} />,
		tagBg: '#DBEAFE',
		tagColor: '#2563EB'
	},
	CARD: {
		key: 'card',
		label: 'Card',
		singular: 'Card',
		icon: (color, size = 13) => <ZezamiiIcon name='card' size={size} color={color} strokeWidth={2} />,
		tagBg: '#E0F2FE',
		tagColor: '#0369A1'
	},
	PIN: {
		key: 'pin',
		label: 'PIN',
		singular: 'Passcode',
		icon: (color, size = 13) => <ZezamiiIcon name='pin' size={size} color={color} strokeWidth={2} />,
		// Violet, NOT the previous rose red — a passcode tag must never read as an
		// error/danger state next to green (mobile) and blue (card / cloud key).
		tagBg: '#F5F3FF',
		tagColor: '#6D28D9'
	},
	CLOUD_KEY: {
		key: 'cloudKey',
		label: 'Cloud Key',
		singular: 'Cloud Key',
		icon: (color, size = 13) => <CloudOutlined style={{ fontSize: size, color }} />,
		tagBg: '#F0F9FF',
		tagColor: '#0369A1'
	}
});

export const CREDENTIAL_TYPES = Object.freeze(
	Object.values(CREDENTIAL).reduce((acc, cred) => {
		acc[cred.key] = cred;
		return acc;
	}, {})
);
